import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { api } from "@/lib/api";
import Seo from "@/components/Seo";
import ArticleCard from "@/components/ArticleCard";
import { CHALKOS_URL, CHALKOS_SIGNUP_URL, CHALKOS_FEATURES } from "@/lib/chalkos";
import { trackChalkos } from "@/lib/analytics";

const AUDIENCES = [
  { label: "For youth teams", desc: "Coaches, parents, and guardians on one squad. Run practice, send updates, and catch schedule conflicts before they happen." },
  { label: "For team parents", desc: "The person quietly running the team gets volunteer sign-ups, snack rotations, and RSVPs handled in one place." },
  { label: "For leagues", desc: "Commissioners get league-wide broadcasts, every coach on one board, and season archives on autopilot." },
];

export default function Chalkos() {
  const [related, setRelated] = useState([]);

  useEffect(() => {
    trackChalkos("chalkos_landing_view", { source_page: "/chalkos" });
    (async () => {
      try {
        const [cc, pp] = await Promise.all([
          api.get("/articles?category=Coaches%20Corner&limit=6"),
          api.get("/articles?category=Practice%20Plans&limit=6"),
        ]);
        const merged = [...(cc.data || []), ...(pp.data || [])];
        const seen = new Set();
        setRelated(merged.filter((a) => !seen.has(a.slug) && seen.add(a.slug)).slice(0, 6));
      } catch {
        setRelated([]);
      }
    })();
  }, []);

  const outbound = (destination, cta) => () =>
    trackChalkos("chalkos_outbound_click", { cta, source_page: "/chalkos", destination });

  return (
    <div data-testid="chalkos-page">
      <Seo
        title="ChalkOS — Team management for youth-sports coaches"
        description="ChalkOS, a Voluntold coaching tool. Schedules, team communication, rosters, RSVPs, volunteers, and season organization — one app for the people actually running youth sports."
        path="/chalkos"
      />

      {/* Hero */}
      <section className="bg-[#222222]" data-testid="chalkos-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <span className="font-head uppercase tracking-[0.2em] text-xs text-[#DB9130]">ChalkOS · a Voluntold coaching tool</span>
          <h1 className="mt-4 font-head font-semibold uppercase leading-[1.03] text-4xl sm:text-5xl lg:text-6xl text-[#F5F0EA] max-w-4xl">
            Built for the coaches doing everything.
          </h1>
          <p className="mt-5 text-[#A9B6BF] text-lg lg:text-xl max-w-2xl leading-relaxed">
            Spend less time managing the team. More time coaching it. Voluntold talks about the realities of youth
            sports — ChalkOS handles the work that comes with running the team.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <a
              href={CHALKOS_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="chalkos-hero-explore"
              onClick={outbound(CHALKOS_URL, "explore")}
              className="inline-flex items-center gap-2 bg-[#DB9130] text-[#1A1A1A] font-head uppercase tracking-wide text-sm px-6 py-3 hover:bg-[#E8A33D] transition-colors"
            >
              Explore ChalkOS <ArrowUpRight size={16} />
            </a>
            <a
              href={CHALKOS_SIGNUP_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="chalkos-hero-startfree"
              onClick={outbound(CHALKOS_SIGNUP_URL, "start_free")}
              className="inline-flex items-center gap-2 border-2 border-[#2D2D2D] text-[#F5F0EA] font-head uppercase tracking-wide text-sm px-6 py-3 hover:border-[#DB9130] hover:text-[#DB9130] transition-colors"
            >
              Start Free
            </a>
            <Link
              to="/category/coaches-corner"
              data-testid="chalkos-hero-resources"
              className="inline-flex items-center gap-1 text-[#F5F0EA] hover:text-[#DB9130] link-line text-sm ml-1 transition-colors"
            >
              See coach resources <ArrowRight size={15} />
            </Link>
          </div>
          <p className="mt-3 text-xs text-[#8A97A0]">Start free. No credit card required.</p>
        </div>
      </section>

      {/* Relationship line */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 border-b border-[#D3CFC0]">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-start">
          <div>
            <span className="kicker">Voluntold</span>
            <p className="mt-2 font-head uppercase text-xl text-[#1E1E1E] leading-tight">Talks about the realities of coaching youth sports.</p>
          </div>
          <div>
            <span className="kicker text-[#DB9130]">ChalkOS</span>
            <p className="mt-2 font-head uppercase text-xl text-[#1E1E1E] leading-tight">Handles the work that comes with actually running the team.</p>
          </div>
        </div>
      </section>

      {/* Feature grid */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14" data-testid="chalkos-features">
        <h2 className="font-head font-semibold uppercase text-2xl lg:text-3xl text-[#1E1E1E] border-b-2 border-[#D3CFC0] pb-3 mb-8">
          Built for the way coaches actually work
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {CHALKOS_FEATURES.map((f) => (
            <div key={f.name} data-testid={`chalkos-feature-${f.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className="border-2 border-[#D3CFC0] bg-[#E7E3D9] p-5">
              <h3 className="font-head font-semibold uppercase text-base text-[#1E1E1E]">{f.name}</h3>
              <p className="mt-2 text-[#6E6E6E] text-sm leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Who it's for */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-14" data-testid="chalkos-audiences">
        <h2 className="font-head font-semibold uppercase text-2xl lg:text-3xl text-[#1E1E1E] border-b-2 border-[#D3CFC0] pb-3 mb-8">
          Who it's for
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {AUDIENCES.map((a) => (
            <div key={a.label} className="border-l-4 border-[#DB9130] bg-[#F5F0EA] p-5">
              <h3 className="font-head uppercase text-lg text-[#222222]">{a.label}</h3>
              <p className="mt-2 text-[#6E6E6E] text-sm leading-relaxed">{a.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* From Voluntold — bridge back into content */}
      {related.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16" data-testid="chalkos-related">
          <div className="flex items-end justify-between border-b-2 border-[#D3CFC0] pb-3 mb-8">
            <h2 className="font-head font-semibold uppercase text-2xl lg:text-3xl text-[#1E1E1E]">From Voluntold</h2>
            <span className="kicker">Reads for coaches running teams</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {related.map((a, i) => <ArticleCard key={a.slug} article={a} testId={`chalkos-related-${i}`} />)}
          </div>
        </section>
      )}

      {/* Final CTA band */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16" data-testid="chalkos-final-cta">
        <div className="border-2 border-[#222222] bg-[#222222] p-8 sm:p-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5">
          <div>
            <h2 className="font-head font-semibold uppercase text-2xl text-[#F5F0EA] leading-tight">One app. Every team. Every season.</h2>
            <p className="mt-2 text-[#A9B6BF] text-sm">Start free and upgrade when you're ready.</p>
          </div>
          <a
            href={CHALKOS_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="chalkos-final-explore"
            onClick={outbound(CHALKOS_URL, "explore_footer")}
            className="inline-flex items-center gap-2 bg-[#DB9130] text-[#1A1A1A] font-head uppercase tracking-wide text-sm px-6 py-3 hover:bg-[#E8A33D] transition-colors shrink-0"
          >
            Explore ChalkOS <ArrowUpRight size={16} />
          </a>
        </div>
      </section>
    </div>
  );
}
